/**
 * The chart shell's own styling — panel frame, chart box, scroller, legend,
 * tooltip card, empty and skeleton states, and the table fallback.
 *
 * ── THE SIZING RULE, AND WHY IT IS SHAPED THIS WAY ──
 *
 * /dashboard/reports shipped its charts as
 * `<ResponsiveContainer width="90%" height="60%">` in ordinary flow. Recharts
 * resolves a percentage height against the PARENT's height, and the parent chain
 * goes `overflow: visible` under md inside a `LayoutContainer` that is
 * `height: auto` under md. `height: 100%` of an auto-height parent is auto, and
 * 60% of auto is ZERO — so both charts were invisible on every phone and tablet
 * while looking perfectly correct on a laptop.
 *
 * The fix is that the WRAPPER must carry a DEFINITE height. `aspect-ratio` gives
 * one that scales with the width, and `min-height` is the floor that guarantees
 * it is definite even where `aspect-ratio` is unsupported or the container has
 * no resolved width yet. `dashboard-screen/chart-sizing.test.ts` asserts the property.
 */
import styled from '@emotion/styled';
import { Staleable as SharedStaleable } from '../../../../../motion';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  transition,
  typography,
  zIndex,
} from '../../../../../style-utils/theme';
import { down, touch, up } from '../../../../../style-utils/media-queries';
import { skeletonBackground } from '../../../../../style-utils/motion';

/* ─────────────────────────────  panel frame  ──────────────────────────── */

export const Panel = styled.section<{ wide?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  /* minmax(0,1fr) elsewhere plus this: a bare grid track has a min-content floor
     and one unbreakable label bleeds the panel sideways. */
  min-width: 0;
  padding: ${spacing.sm};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.xs};
  overflow-wrap: anywhere;

  ${up('md')} {
    padding: ${spacing.md};
    grid-column: ${({ wide }) => (wide ? '1 / -1' : 'auto')};
  }
`;

export const PanelHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing.sm};
  flex-wrap: wrap;
`;

export const PanelTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  min-width: 0;
  flex: 1 1 240px;
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.bold};
  letter-spacing: ${typography.tracking.snug};
  color: ${palette.ink};
`;

/** PERMANENT VISIBLE COPY, NOT A HOVER. Several panels on this screen carry a
 *  sentence that changes what their number means, and a tooltip is where a
 *  caveat goes to be missed. */
export const PanelCaveat = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const PanelControls = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing.xs};
  flex-wrap: wrap;

  /* IT MUST BE ALLOWED TO SHRINK, or wrapping never happens.
     This was flex:0 0 auto, which pins the box at its max-content width —
     the legend, the Chart/Table toggle and the two export buttons all on one
     line, 499px of it. flex-wrap on the box itself cannot help, because
     nothing ever makes the box narrow enough to wrap inside. The result was 173
     pixels of HORIZONTAL PAGE SCROLL at 375px, on a screen whose brief forbids
     any at 320.

     min-width:0 is the other half: a flex item's automatic minimum size is
     its min-content width, which for a row of buttons is the widest button plus
     the gaps, and that floor is what stops the shrink from taking effect. */
  flex: 1 1 auto;
  min-width: 0;
`;

export const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
  min-width: 0;
`;

/* The notes list belongs to the PANEL, never pooled at the bottom of the page:
   a caveat five panels away from its number is a caveat about nothing. */
export const NoteList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

export const NoteItem = styled.li`
  display: flex;
  gap: ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};

  &::before {
    content: '·';
    color: ${palette.slateLight};
  }
`;

/* ────────────────────────────  the chart box  ─────────────────────────── */

/**
 * The scroller. 24 categorical labels need about 640px before they stop
 * overlapping into a smear, and the choice at 375px is a scrollbar inside the
 * panel or a horizontal scrollbar on the entire application.
 */
export const ChartScroller = styled.div`
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  /* ── THE SECOND AXIS IS NOT OPTIONAL, IT IS INHERITED ──
     CSS does not let one axis scroll while the other stays 'visible': setting
     'overflow-x: auto' alone makes the computed 'overflow-y' 'auto' too. So this
     box quietly grew a VERTICAL scrollbar as well, and every panel was
     overshooting its own height by ~5px (measured: client 280 / scroll 285 on
     traffic, 260/265 on composition, 240/245 on severity) — enough for a live
     scrollbar, not enough to show anything. Reported as "there's overflow on the
     chart components". The overshoot itself is fixed at source below; this line
     is what stops the next 1px of rounding from putting the bar back. */
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;

  /* A CHART THAT SCROLLS HAS TO SAY SO, AND THE SCROLLBAR IS THE SAYING.
     This box exists so a chart with a real minimum width scrolls inside itself
     rather than letting the PAGE scroll sideways — point 2 of chart-shell's
     header, and deliberate. What was missing was any signal that it happened.
     scripts/analytics/responsive.js measured these on the Trends tab at every
     width, phone to laptop: +418px on the hour panel at 320, +298 on dwell, +82
     on a 1280 laptop, with no scrollbar, no peek, no mask and no shade.

     A REAL SCROLLBAR RATHER THAN A FADE, deliberately. The overlay bars macOS
     and iOS draw are invisible until you are already scrolling, which is no use
     to somebody deciding whether to try. An explicit thin bar appears exactly
     when there is overflow and costs nothing when there is not — and it needs no
     ResizeObserver, no ref and no measurement, which is what the first attempt
     here used and could not make fire reliably against a chart sized after mount.

     It takes HEIGHT, not width: this is a horizontal scroller. The panel's own
     min-height absorbs it. */
  scrollbar-width: thin;
  scrollbar-color: ${palette.line} transparent;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${palette.line};
    border-radius: ${radius.pill};
  }

  &:hover::-webkit-scrollbar-thumb {
    background: ${palette.slateLight};
  }
`;

/**
 * THE BOX A CHART FILLS. A definite height, always — see the header.
 *
 * `aspect-ratio` scales it with the container so a wide desktop gets a taller
 * chart without a breakpoint ladder; `min-height` is the floor that keeps the
 * height definite regardless.
 */
export const ChartArea = styled.div<{
  $aspect: number;
  $minHeight: number;
  $minWidth?: number;
}>`
  position: relative;
  width: 100%;
  min-width: ${({ $minWidth }) => ($minWidth ? `${$minWidth}px` : '0')};
  min-height: ${({ $minHeight }) => $minHeight}px;
  aspect-ratio: ${({ $aspect }) => $aspect};

  /* A phone is short as well as narrow; capping the aspect-driven height stops a
     16:9 chart eating a whole viewport. */
  ${down('sm')} {
    max-height: 320px;
  }

  /* ── THE FLOOR ABOVE IS NOT INHERITED, AND THAT COST EVERY PHONE ITS CHART ──
     ResponsiveContainer renders a div styled height:100% and then measures
     ITSELF to size the SVG. In WebKit a percentage height inside a box whose own
     height comes from aspect-ratio resolves against the ASPECT-DERIVED height:
     the min-height above is applied to THIS element and the child never sees it.
     Measured on a 320px phone, this box was correctly 280px tall and the chart
     inside it was 222 / 2.8 = 79px. Nothing overflowed, nothing was clipped,
     nothing scrolled sideways — the chart was simply a third of its height, and
     every width-based check in the suite passed.

     CHROMIUM DOES NOT DO THIS, which is why it survived 317 browser checks. The
     same page at 390px, same build, one property apart: WebKit 104px inside a
     280px box, Chromium 280px. So this is not a workaround for a bug in the
     chart library — it is the reason a Safari pass exists at all.

     Out of flow, the container fills the USED height instead of re-deriving it,
     so the floor reaches the chart. position:relative above is what makes this
     box the containing block; it was already here for the tooltip.

     DO NOT REPLACE THIS WITH A HEIGHT ON THE CONTAINER. Recharts reads its own
     offsetHeight, so a value it can resolve is the whole requirement, and a
     fixed one would throw away the aspect-ratio scaling on a wide desk. */
  > .recharts-responsive-container {
    position: absolute;
    inset: 0;
  }

  /* ── WHERE THE ~5px OF OVERFLOW CAME FROM ──
     Recharts renders a bare <svg>, and an <svg> is an INLINE box. An inline box
     sits on the text baseline, so the line it is in reserves room underneath it
     for descenders — about 5px at this font-size. The wrapper is sized exactly
     to the chart ('inset: 0' above), so that reserved strip is pure overflow:
     '.recharts-wrapper' measured client 280 / scroll 285 on every panel, which
     is what the scroller above turned into a vertical scrollbar.
     One property removes the line box entirely, at the source, for every chart
     in the screen rather than per panel. */
  svg {
    display: block;
  }
`;

/* ──────────────────────────────  tooltip  ─────────────────────────────── */

export const TipCard = styled.div`
  min-width: 160px;
  max-width: 280px;
  padding: ${spacing.xs};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  box-shadow: ${shadow.md};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  color: ${palette.ink};
  pointer-events: none;
`;

export const TipLabel = styled.div`
  font-weight: ${typography.weight.bold};
  margin-bottom: ${spacing.half};
  color: ${palette.ink};
`;

export const TipRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${spacing.half};
  line-height: ${typography.leading.snug};
  color: ${palette.slate};
`;

export const TipStrong = styled.strong`
  font-weight: ${typography.weight.bold};
  font-variant-numeric: tabular-nums;
  color: ${palette.ink};
`;

export const TipSwatch = styled.span<{ tone: string; dashed?: boolean }>`
  flex: 0 0 auto;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ tone, dashed }) => (dashed ? 'transparent' : tone)};
  border: ${({ tone, dashed }) => (dashed ? `2px dashed ${tone}` : 'none')};
`;

/** A sentence under the rows. Where the tooltip has to say what the numbers
 *  above it do NOT mean. */
export const TipNote = styled.div`
  margin-top: ${spacing.half};
  padding-top: ${spacing.half};
  border-top: 1px solid ${palette.line};
  color: ${palette.slateLight};
  line-height: ${typography.leading.normal};
`;

/* ───────────────────────────────  legend  ─────────────────────────────── */

export const LegendRow = styled.div<{ $below?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.xs};
  align-items: center;
  ${({ $below }) => ($below ? `margin-top: ${spacing.xs};` : '')}
`;

/**
 * A legend entry is a BUTTON, because it toggles its series.
 * `aria-pressed` carries the state; the strike-through and the drained swatch
 * carry it for everybody else — a hidden series must not read as a series that
 * happens to be flat.
 */
export const LegendItem = styled.button<{ $off?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: ${layout.compactControlHeight};
  padding: 0 ${spacing.half};
  border: 1px solid transparent;
  border-radius: ${radius.pill};
  background: none;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.medium};
  color: ${({ $off }) => ($off ? palette.slateLight : palette.slate)};
  text-decoration: ${({ $off }) => ($off ? 'line-through' : 'none')};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
    border-color: ${palette.line};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  /* Dense by design at a mouse, wrong under a thumb. */
  ${touch} {
    min-height: ${layout.touchTarget};
    padding: 0 ${spacing.xs};
  }
`;

export const LegendSwatch = styled.span<{
  tone: string;
  dashed?: boolean;
  $off?: boolean;
}>`
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  opacity: ${({ $off }) => ($off ? 0.35 : 1)};
  background: ${({ tone, dashed }) => (dashed ? 'transparent' : tone)};
  border: ${({ tone, dashed }) => (dashed ? `2px dashed ${tone}` : 'none')};
`;

export const LegendValue = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${palette.ink};
  font-weight: ${typography.weight.semibold};
`;

/* ─────────────────────────  empty / error / busy  ─────────────────────── */

export const StateBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${spacing.xs};
  text-align: center;
  padding: ${spacing.lg} ${spacing.sm};
  min-height: 180px;
  border: 1px dashed ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surfaceMuted};
`;

/** A CHART WITH NO DATA MUST NEVER RENDER AS AN EMPTY GRID. Five inert bars say
 *  "this is where a chart goes" without pretending to be one. */
export const EmptyGlyph = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${spacing.half};
  height: 48px;
  margin-bottom: ${spacing.half};
`;

export const EmptyBar = styled.span<{ h: number }>`
  display: block;
  width: 8px;
  height: ${({ h }) => h}px;
  border-radius: 2px;
  background: ${palette.line};
`;

export const StateTitle = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

export const StateText = styled.p`
  margin: 0;
  max-width: 44ch;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

export const FailureBox = styled(StateBox)`
  border-style: solid;
  border-color: ${palette.alertSoft};
  background: ${palette.alertSoft};
`;

export const SkeletonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  width: 100%;
`;

export const Skeleton = styled.span<{ h: number; w?: string }>`
  display: block;
  width: ${({ w }) => w || '100%'};
  height: ${({ h }) => h}px;
  border-radius: ${radius.xs};
  ${skeletonBackground}
`;

/** The chart dims while a refetch is in flight. NOT hidden: what is on screen is
 *  still the last true answer, and blanking it is a worse lie than showing it a
 *  moment stale. The dimming itself is the shared motion primitive (it also
 *  sets aria-busy); this only adds the box rules the panel chain depends on. */
export const Staleable = styled(SharedStaleable)`
  min-width: 0;
  /* THE CONTAINING BLOCK FOR THE PANEL'S <SrOnly> SUMMARY, and it is load-bearing
     rather than decorative — see the SrOnly comment below. Without a positioned
     ancestor anywhere in the panel chain, that absolutely positioned span
     resolved against the initial containing block, escaped the dashboard's
     scroll port and stretched the document by 1,369px of blank canvas. */
  position: relative;
`;

/* ───────────────────────────  panel controls  ─────────────────────────── */

export const PanelToggle = styled.button<{ active?: boolean }>`
  min-height: ${layout.compactControlHeight};
  padding: 0 ${spacing.xs};
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  color: ${({ active }) => (active ? palette.irisDeep : palette.slate)};
  background: ${({ active }) => (active ? palette.irisTint : 'transparent')};
  border: 1px solid ${({ active }) => (active ? palette.iris : palette.line)};
  border-radius: ${radius.pill};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lagoon};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
    padding: 0 ${spacing.sm};
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.half};
  min-height: ${layout.touchTarget};
  padding: 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.lagoon};
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  cursor: pointer;
  transition: ${transition.colors};

  &:hover:not(:disabled) {
    background: ${palette.irisTintSoft};
    border-color: ${palette.lagoon};
  }
  &:disabled {
    color: ${palette.slateLight};
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
`;

/** Per-panel export, revealed on hover and on keyboard focus — never on hover
 *  alone, which is a control a keyboard user cannot reach. */
export const PanelActions = styled.div`
  display: flex;
  gap: ${spacing.half};
  opacity: 0;
  transition: opacity ${duration.fast} ${easing.standard};

  /* THE PANEL IS MATCHED BY ITS ATTRIBUTE, NOT BY A COMPONENT SELECTOR.
     Interpolating a styled component into a selector is emotion's
     component-selector syntax, and it needs
     @emotion/babel-plugin, which this build does not have. Without the plugin
     emotion does not degrade — it THROWS at serialize time, on first render,
     and with no error boundary above it the whole application unmounts to a
     blank page.

     entity-gallery.styled.tsx:63 and dashboard-home.styled.tsx:537 both record
     this same trap. It is a house rule and this file broke it; jsdom never
     serialises the rule, so 863 unit tests were green over a blank app.

     The attribute is the one every panel already carries and the one the sizing
     guard keys on, so it cannot drift away from the element it names. */
  [data-testid^='panel-']:hover &,
  [data-testid^='panel-']:focus-within & {
    opacity: 1;
  }

  /* There is no hover on a touch screen, so they are simply always visible. */
  ${touch} {
    opacity: 1;
  }
`;

/* ────────────────────────────  table fallback  ────────────────────────── */

export const TableScroller = styled.div`
  width: 100%;
  min-width: 0;
  max-height: 320px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
`;

export const DataTable = styled.table<{ $dense?: boolean; $wrapHeads?: boolean }>`
  width: 100%;

  /* THE DENSITY SETTING, FINALLY ATTACHED TO SOMETHING.
     density has been in the filter model, validated, serialised to the URL as
     dn and persisted to the device prefs for the whole life of this screen with
     nothing reading it - a preference that survived a reload and a shared link
     and changed not one pixel.

     It rides the TABLE rather than each cell so the header, the body and the
     trailing action column stay in step, and a descendant selector outranks the
     padding Th and Td set for themselves. Comfortable is the default and
     resolves to the same 8px those cells already used, so no existing view
     moves. */
  --cell-pad-block: ${({ $dense }) => ($dense ? spacing.half : spacing.xs)};

  th,
  td {
    padding: var(--cell-pad-block) ${spacing.xs};
  }

  /* separate, NOT collapse: under border-collapse the cell borders belong to the
     table rather than the cell, so a sticky th scrolls away from its own bottom
     hairline. */
  border-collapse: separate;
  border-spacing: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.ink};

  thead th {
    position: sticky;
    top: 0;
    z-index: ${zIndex.base};
    background: ${palette.surfaceMuted};
  }

  /* HEADERS THAT MAY WRAP (the Records table). Th never wraps, so on a wide
     book its longest caption ("PRE-AUTHORISED", 136px) set a column's width and
     the table, held to 100%, took it back from the DATA: at 1280 the dashboard
     gives Records 860px, and with Plan 5's "On pass" column the Date cell stood
     51px wide and three lines tall (the persona journey's screen judge). A
     caption that breaks after its hyphen is cheaper than a squeezed row —
     measured: 0 squeezed cells with eleven or twelve columns, and no sideways
     scroll. */
  ${({ $wrapHeads }) => ($wrapHeads ? 'thead th { white-space: normal; }' : '')}
`;

export const Th = styled.th<{ numeric?: boolean }>`
  padding: ${spacing.xs};
  text-align: ${({ numeric }) => (numeric ? 'right' : 'left')};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
  border-bottom: 1px solid ${palette.line};
  white-space: nowrap;
`;

export const Td = styled.td<{ numeric?: boolean }>`
  padding: ${spacing.xs};
  text-align: ${({ numeric }) => (numeric ? 'right' : 'left')};
  font-variant-numeric: ${({ numeric }) => (numeric ? 'tabular-nums' : 'normal')};
  border-bottom: 1px solid ${palette.line};
  vertical-align: top;
`;

export const TotalRow = styled.tr`
  td {
    font-weight: ${typography.weight.bold};
    background: ${palette.surfaceMuted};
  }
`;

/** Screen-reader-only, for a chart's accessible summary. */
/* ── THIS IS WHAT PUT A WHITE BAND UNDER THE DASHBOARD ──
 *
 * `position: absolute` with NO `top`/`left` resolves to the box's STATIC
 * position — where it would have sat in flow — measured against its containing
 * block. Nothing in the chart panel chain is positioned (`Staleable`, `PanelBody`
 * and `Panel` were all `position: static`), so the containing block resolved all
 * the way up to the INITIAL one. That has two consequences, and the second is
 * the bug:
 *
 *   1. The span is not clipped by `DashboardContentContainer`, the real scroll
 *      port, because that box is not its containing block either.
 *   2. Its static position — roughly 2,268px down inside a 4,254px scroller — is
 *      therefore a DOCUMENT coordinate. One 1px span parked there stretched
 *      <html> to 2,269px over a 900px viewport.
 *
 * Measured on the running app: `html.scrollHeight` 2269 / `clientHeight` 900, so
 * 1,369px of scroll range below an app shell that is exactly 900px tall, and
 * `elementFromPoint` at every y in that band returned <html> — bare canvas. It
 * appeared only ~10s in, when the last panels rendered their summaries, which is
 * why it read as intermittent.
 *
 * TWO belts, because either alone would leave the trap armed for the next
 * caller: `top/left: 0` means even an unpositioned ancestor chain parks this at
 * the containing block's ORIGIN rather than 2,268px down it, and `Staleable` is
 * now `position: relative` so the origin is the panel it describes.
 */
export const SrOnly = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  /* 'clip' is deprecated but is the only form pre-Safari-14.1 honours; the
     modern property wins where both are understood. */
  clip: rect(0, 0, 0, 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

/* ────────────────────────────────  the ring  ─────────────────────────── */

/* The chart fills the box; the figure sits on top of it, centred, and is
   'pointer-events: none' so it never eats a hover the ring should have had. */
export const RingBox = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  /* A DEFINITE HEIGHT, never a percentage: ResponsiveContainer measures its
     parent, and a parent whose own height is a percentage of an auto-height
     grandparent measures zero. That is the bug chart-sizing.test.ts exists to
     prevent, and a ring is exactly the shape it bit first. */
  min-height: 168px;
  aspect-ratio: 1;

  /* Same inline-<svg> descender strip ChartArea documents: measured 322px of box
     against 327px of content on both rings of the shelf panel. A ring is not
     inside ChartScroller, so it never grew a scrollbar — it simply bled 5px past
     its own square. */
  svg {
    display: block;
  }
`;

export const RingCentre = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  pointer-events: none;
`;

export const RingFigure = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.xl}px;
  font-weight: ${typography.weight.bold};
  font-variant-numeric: tabular-nums;
  line-height: 1;
  color: ${palette.ink};
`;

export const RingUnit = styled.span`
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.slate};
`;

export const RingNote = styled.p`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.xxs}px;
  line-height: ${typography.leading.snug};
  color: ${palette.slate};
  text-align: center;
`;
